<template>
  <div class="recharge-page">
    <!-- 顶部导航 -->
    <div class="nav-header">
      <button class="back-btn" @click="goBack">
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path fill="#333" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
        </svg>
      </button>
      <span class="nav-title">{{ orderCreated ? '支付订单' : 'USDT充值' }}</span>
      <div class="nav-right"></div>
    </div>

    <!-- 输入金额界面 -->
    <div class="input-view" v-if="!orderCreated">
      <div class="amount-display">
        <span class="amount-value">{{ displayAmount || '0' }}</span>
        <span class="currency">USDT</span>
        <span class="cursor" v-if="!displayAmount"></span>
      </div>
      <div class="min-hint">最低充值 1 USDT</div>

      <!-- 数字键盘 -->
      <div class="keyboard">
        <div class="key" @click="inputKey('1')">1</div>
        <div class="key" @click="inputKey('2')">2</div>
        <div class="key" @click="inputKey('3')">3</div>
        <div class="key" @click="inputKey('4')">4</div>
        <div class="key" @click="inputKey('5')">5</div>
        <div class="key" @click="inputKey('6')">6</div>
        <div class="key" @click="inputKey('7')">7</div>
        <div class="key" @click="inputKey('8')">8</div>
        <div class="key" @click="inputKey('9')">9</div>
        <div class="key" @click="inputKey('.')">.</div>
        <div class="key" @click="inputKey('0')">0</div>
        <div class="key delete" @click="deleteKey">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="#333" d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"/>
          </svg>
        </div>
      </div>

      <!-- 确认按钮 -->
      <button class="confirm-btn" :disabled="!canCreate" @click="createOrder">
        {{ creating ? '创建中...' : '确认充值' }}
      </button>
    </div>

    <!-- 支付界面 -->
    <div class="pay-view" v-else>
      <div class="pay-card">
        <div class="pay-amount">
          <div class="pay-label">需转账金额</div>
          <div class="pay-usdt">{{ exactUsdtAmount }} <span>USDT</span></div>
        </div>

        <div class="qrcode-section">
          <canvas ref="qrcodeCanvas"></canvas>
          <div class="address-text">{{ tronAddress }}</div>
          <button class="copy-btn" @click="copyAddress">复制地址</button>
        </div>

        <div class="order-info">
          <div class="info-row">
            <span>订单号</span>
            <span>{{ orderId }}</span>
          </div>
          <div class="info-row">
            <span>有效期</span>
            <span class="expire">{{ expireMinutes }}分钟</span>
          </div>
          <div class="info-row">
            <span>状态</span>
            <span :class="'status-' + orderStatus">{{ orderStatusText }}</span>
          </div>
        </div>
      </div>

      <div class="tips">
        <p>• 请精确转账 <strong>{{ exactUsdtAmount }} USDT</strong></p>
        <p>• 仅支持 <strong>TRC20</strong> 网络</p>
        <p>• 转账后1-5分钟自动到账</p>
        <p>• 到账可能有延迟，如有问题联系 <strong>@baicainb886</strong></p>
      </div>

      <div class="pay-actions">
        <button class="check-btn" @click="checkPayment" :disabled="checking">
          {{ checking ? '查询中...' : '我已支付' }}
        </button>
        <button class="cancel-btn" @click="cancelOrder">取消</button>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/config/api'
import QRCode from 'qrcode'

export default {
  name: 'RechargePage',
  props: {
    currentBalance: {
      type: [String, Number],
      default: '0.00'
    }
  },
  data() {
    return {
      inputAmount: '',
      orderCreated: false,
      orderId: '',
      tronAddress: '',
      expireMinutes: 30,
      orderStatus: 'pending',
      exactUsdtAmount: 0,
      creating: false,
      checking: false,
      checkTimer: null
    }
  },
  computed: {
    displayAmount() {
      return this.inputAmount
    },
    finalAmount() {
      return parseFloat(this.inputAmount) || 0
    },
    canCreate() {
      return this.finalAmount >= 1 && !this.creating
    },
    orderStatusText() {
      const map = { pending: '等待支付', paid: '已到账', expired: '已过期' }
      return map[this.orderStatus] || '未知'
    }
  },
  mounted() {
    this.loadConfig()
  },
  beforeUnmount() {
    if (this.checkTimer) clearInterval(this.checkTimer)
  },
  methods: {
    async loadConfig() {
      try {
        const res = await apiFetch('/api/recharge/config')
        if (res.code === 0 && res.data) {
          this.expireMinutes = res.data.expireMinutes || 30
        }
      } catch (e) { console.log(e) }
    },
    inputKey(key) {
      if (key === '.') {
        if (this.inputAmount.includes('.')) return
        if (!this.inputAmount) this.inputAmount = '0'
      }
      if (this.inputAmount.includes('.')) {
        const decimal = this.inputAmount.split('.')[1]
        if (decimal && decimal.length >= 2) return
      }
      if (this.inputAmount.length >= 8) return
      this.inputAmount += key
    },
    deleteKey() {
      this.inputAmount = this.inputAmount.slice(0, -1)
    },
    goBack() {
      if (this.orderCreated) {
        this.orderCreated = false
      } else {
        this.$emit('close')
      }
    },
    async createOrder() {
      if (!this.canCreate) return
      this.creating = true
      try {
        const res = await apiFetch('/api/recharge/create', {
          method: 'POST',
          body: JSON.stringify({
            usdtAmount: this.finalAmount
          })
        })
        if (res.code === 0 && res.data) {
          console.log('创建订单返回数据:', res.data)
          this.orderCreated = true
          this.orderId = res.data.orderId
          this.tronAddress = res.data.address
          this.exactUsdtAmount = res.data.usdtAmount
          this.$nextTick(() => this.generateQRCode())
          this.startCheckTimer()
        } else {
          alert(res.msg || '创建失败')
        }
      } catch (e) {
        alert('创建失败')
      } finally {
        this.creating = false
      }
    },
    generateQRCode() {
      const canvas = this.$refs.qrcodeCanvas
      if (canvas && this.tronAddress) {
        QRCode.toCanvas(canvas, this.tronAddress, { width: 160, margin: 2 })
      }
    },
    copyAddress() {
      navigator.clipboard.writeText(this.tronAddress).then(() => alert('已复制'))
        .catch(() => {
          const input = document.createElement('input')
          input.value = this.tronAddress
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
          alert('已复制')
        })
    },
    startCheckTimer() {
      this.checkTimer = setInterval(() => this.checkPaymentSilent(), 10000)
    },
    async checkPaymentSilent() {
      try {
        const res = await apiFetch(`/api/recharge/status/${this.orderId}`)
        if (res.code === 0 && res.data) {
          this.orderStatus = res.data.status
          if (res.data.status === 'paid') {
            clearInterval(this.checkTimer)
            alert('充值成功！')
            this.$emit('close')
          } else if (res.data.status === 'expired') {
            clearInterval(this.checkTimer)
          }
        }
      } catch (e) { console.log(e) }
    },
    async checkPayment() {
      this.checking = true
      try {
        const res = await apiFetch(`/api/recharge/status/${this.orderId}`)
        if (res.code === 0 && res.data) {
          this.orderStatus = res.data.status
          if (res.data.status === 'paid') {
            alert('充值成功！')
            this.$emit('close')
          } else if (res.data.status === 'pending') {
            alert('暂未检测到付款')
          } else if (res.data.status === 'expired') {
            alert('订单已过期')
          }
        }
      } catch (e) {
        alert('查询失败')
      } finally {
        this.checking = false
      }
    },
    cancelOrder() {
      if (confirm('确定取消？')) {
        this.orderCreated = false
        this.inputAmount = ''
        if (this.checkTimer) clearInterval(this.checkTimer)
      }
    }
  }
}
</script>

<style scoped>
.recharge-page {
  min-height: 100%;
  height: 100%;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: env(safe-area-inset-bottom);
}

.nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #eee;
}

.back-btn {
  background: none;
  border: none;
  padding: 4px;
}

.nav-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}

.nav-right { width: 32px; }

.input-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.amount-display {
  padding: 40px 20px 10px;
  text-align: center;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
}

.amount-value {
  font-size: 48px;
  font-weight: 500;
  color: #333;
  min-width: 50px;
}

.currency {
  font-size: 20px;
  color: #666;
}

.cursor {
  display: inline-block;
  width: 2px;
  height: 40px;
  background: #1677ff;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

.min-hint {
  text-align: center;
  color: #999;
  font-size: 13px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.keyboard {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #d9d9d9;
  margin-top: auto;
}

.key {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #333;
  background: #fff;
  user-select: none;
  cursor: pointer;
}

.key:active { background: #e5e5e5; }
.key.delete { background: #f5f5f5; }

.confirm-btn {
  margin: 16px;
  height: 48px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 17px;
  font-weight: 500;
}

.confirm-btn:disabled { background: #ccc; }

.pay-view {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: calc(32px + env(safe-area-inset-bottom));
}

.pay-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
}

.pay-amount {
  padding: 24px;
  text-align: center;
  background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
  color: #fff;
}

.pay-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.pay-usdt {
  font-size: 36px;
  font-weight: 600;
}

.pay-usdt span {
  font-size: 16px;
  font-weight: 400;
}

.qrcode-section {
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
}

.address-text {
  font-family: monospace;
  font-size: 11px;
  color: #666;
  word-break: break-all;
  margin: 12px 0;
  padding: 0 20px;
}

.copy-btn {
  background: #f5f5f5;
  border: none;
  padding: 8px 24px;
  border-radius: 16px;
  font-size: 14px;
  color: #1677ff;
}

.order-info { padding: 12px 16px; }

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: #666;
}

.expire { color: #ff9800; }
.status-pending { color: #ff9800; }
.status-paid { color: #52c41a; font-weight: 600; }
.status-expired { color: #999; }

.tips {
  margin: 16px 0;
  padding: 12px 16px;
  background: #fffbe6;
  border-radius: 8px;
}

.tips p {
  margin: 4px 0;
  font-size: 13px;
  color: #666;
}

.tips strong { color: #333; }

.pay-actions {
  display: flex;
  gap: 12px;
}

.check-btn {
  flex: 1;
  height: 44px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
}

.check-btn:disabled { opacity: 0.7; }

.cancel-btn {
  height: 44px;
  padding: 0 20px;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 8px;
  font-size: 16px;
}
</style>
